import { Box, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TagFill } from '@styled-icons/bootstrap';
import { getVisibilityIcon } from 'FeatureModules/Document/utils/documentVisibility';
import { NewTabIcon } from 'Components/Drawer/StyledDrawerItems';
import { ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import React from 'react';
import { Tbooleanish, Booleanish } from 'types/booleanish.types';
import {
  EDocumentCategory,
  EDocumentStatus,
  IDocument,
} from 'FeatureModules/Document/types/document.types';
import { DocumentVisibilityLabels } from 'FeatureModules/Document/utils/document.utils';
import DocumentCategory from '../utils/documentCategory.utils';
import FlexBox from 'Components/FlexBox';

interface IDocumentIntroTitleProps {
  document: IDocument;
  size?: 'small' | 'medium';
  showOpenInNewTab?: boolean;
}

const Status = styled(Typography)<{ status: keyof typeof EDocumentStatus }>(
  ({ status, theme }) => ({
    fontSize: 13,
    marginRight: 8,

    color:
      status === 'VALID'
        ? theme.palette.success.main
        : status === 'EXPIRED'
        ? theme.palette.error.main
        : theme.custom.colors.textLight,
  })
);

const TagIcon = styled(TagFill)(({ theme }) => ({
  marginRight: theme.spacing(0.5),
}));
const CertificateLogo = styled('img')(({ theme }) => ({
  width: 30,
}));

const ClickableBox = styled(Box)<{ 'is-clickable': Tbooleanish }>(
  ({ 'is-clickable': isClickable }) => ({
    cursor: isClickable === 'true' ? 'pointer' : 'default',
  })
);

const DocumentIntroTitle: React.FC<IDocumentIntroTitleProps> = ({
  document,
  size = 'medium',
  showOpenInNewTab,
}) => {
  const { title, category, status, visibility, linkedSites, ownedBy } = document;
  const formattedCategory = category ? DocumentCategory[category].label : '';
  const isCertificate =
    category === EDocumentCategory.CERTIFICATE && document?.certificateCatalogItem;
  const documentTitle = isCertificate && document ? document.certificateCatalogItem?.title : title;

  const handleOpenDocument = () => {
    if (showOpenInNewTab) {
      window.open(document.url || '');
    }
  };

  return (
    <FlexBox gap={1.5}>
      {isCertificate ? (
        <CertificateLogo src={document?.certificateCatalogItem?.imageUrl} />
      ) : (
        <Icon name="document" size={size === 'small' ? 'medium' : 'xx-large'} />
      )}

      <Box>
        <ClickableBox
          display="flex"
          alignItems="center"
          is-clickable={Booleanish(showOpenInNewTab)}
          onClick={handleOpenDocument}
        >
          <Tooltip title={documentTitle || ''}>
            <ThemeTypography variant="ITEM_TITLE" autoOverflow>
              {documentTitle}
            </ThemeTypography>
          </Tooltip>

          {showOpenInNewTab && <NewTabIcon size={14} />}
        </ClickableBox>

        <Box display="flex" alignItems="center" gap={0.8}>
          {status && (
            <Status variant="body2" status={status}>
              {status}
            </Status>
          )}
          {formattedCategory && (
            <ThemeTypography variant="BODY_SMALL" color="GRAY_80" autoOverflow>
              <Box display="flex" alignItems="center" gap={0.5}>
                <TagIcon size={11} />
                {formattedCategory}
              </Box>
            </ThemeTypography>
          )}
          {visibility && (
            <ThemeTypography variant="BODY_SMALL" color="GRAY_80" autoOverflow>
              <Box display="flex" alignItems="center" gap={0.5}>
                {getVisibilityIcon(visibility, 11)}
                {DocumentVisibilityLabels[visibility]}
              </Box>
            </ThemeTypography>
          )}
          {ownedBy && (
            <FlexBox gap={0.5}>
              <Icon name="partner" size="small" />
              <ThemeTypography variant="BODY_SMALL" color="GRAY_80" autoOverflow>
                {ownedBy.name}
              </ThemeTypography>
            </FlexBox>
          )}
          {!!linkedSites?.edges.length && (
            <FlexBox gap={0.5}>
              <Icon name="location" size="small" />
              <ThemeTypography variant="BODY_SMALL" color="GRAY_80" autoOverflow maxWidth={250}>
                {linkedSites.edges
                  .map(({ node: { title } }) => title)
                  .splice(0, 3)
                  .join(', ')}
              </ThemeTypography>
            </FlexBox>
          )}
        </Box>
      </Box>
    </FlexBox>
  );
};

export default DocumentIntroTitle;
